import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { CalendarEvent } from '../../types/calendar';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import CreateEventModal from './CreateEventModal';
import EventDetailsModal from './EventDetailsModal';
import AppLayout from '../layout/AppLayout';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

export default function CalendarPage() {
  useDocumentTitle('Calendar');
  
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const unsubscribe = onSnapshot(
      query(collection(db, 'events'), orderBy('start', 'asc')),
      (snapshot) => {
        const eventData: CalendarEvent[] = [];
        snapshot.forEach((doc) => {
          const event = { id: doc.id, ...doc.data() } as CalendarEvent;
          // Only show public events or private events created by the current user
          if (event.visibility === 'public' || event.createdBy === user.uid) {
            eventData.push(event);
          }
        });
        setEvents(eventData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading events:', error);
        toast.error('Failed to load events');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleDateSelect = (selectInfo: any) => {
    setSelectedDate(selectInfo.start);
    setShowCreateModal(true);
  };

  const handleEventClick = (clickInfo: any) => {
    const event = events.find(e => e.id === clickInfo.event.id);
    if (event) {
      setSelectedEvent(event);
      setShowDetailsModal(true);
    }
  };

  const handleCreateEvent = async (eventData: Partial<CalendarEvent>) => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      await addDoc(collection(db, 'events'), {
        ...eventData,
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      toast.success('Event created successfully');
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event');
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-text-secondary">Loading calendar...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-semibold text-text-primary">Calendar</h1>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                       transition-colors flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Event
            </button>
          </div>

          {/* Calendar */}
          <div className="bg-surface-light rounded-xl shadow-sm p-6">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }}
              events={events.map(event => ({
                id: event.id,
                title: event.title,
                start: event.start,
                end: event.end,
                allDay: event.allDay,
                backgroundColor: event.color || '#3b82f6',
                borderColor: event.color || '#3b82f6',
              }))}
              editable={true}
              selectable={true}
              selectMirror={true}
              dayMaxEvents={true}
              weekends={true}
              select={handleDateSelect}
              eventClick={handleEventClick}
              height="auto"
              contentHeight="auto"
              aspectRatio={2}
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateEventModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateEvent}
          initialDate={selectedDate}
        />
      )}

      {showDetailsModal && selectedEvent && (
        <EventDetailsModal
          event={selectedEvent}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedEvent(null);
          }}
        />
      )}
    </AppLayout>
  );
}